import { useState, useEffect } from 'react'
import { Users, User, Loader, ChevronDown } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Team {
  id: string
  name: string
  description: string
  type: string
  user_role: string
  member_count: number
}

interface TeamMember {
  id: string  // Changed to string for UUID
  first_name: string
  last_name: string
  email: string
  title: string
  team_role: string
}

interface TeamMemberSelectorProps {
  selectedTeamId: string | null
  selectedUserId: string | null  // Changed to string for UUID
  onTeamChange: (teamId: string | null) => void
  onUserChange: (userId: string | null) => void  // Changed to string for UUID
  selectionMode: 'team' | 'individual' | 'both'
  label?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
  showUserRole?: boolean
  allowMultipleUsers?: boolean
  selectedUserIds?: string[]  // Changed to string array for UUIDs
  onMultipleUsersChange?: (userIds: string[]) => void  // Changed to string array for UUIDs
  excludeCurrentUser?: boolean  // New prop for sharing scenarios
}

export default function TeamMemberSelector({
  selectedTeamId,
  selectedUserId,
  onTeamChange,
  onUserChange,
  selectionMode = 'both',
  label = 'Select Team',
  placeholder = 'Choose a team...',
  required = false,
  disabled = false,
  showUserRole = true,
  allowMultipleUsers = false,
  selectedUserIds = [],
  onMultipleUsersChange,
  excludeCurrentUser = false
}: TeamMemberSelectorProps) {
  const [teams, setTeams] = useState<Team[]>([])
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loadingTeams, setLoadingTeams] = useState(false)
  const [loadingMembers, setLoadingMembers] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load teams on component mount
  useEffect(() => {
    loadTeams()
  }, [])

  // Load team members when team changes
  useEffect(() => {
    if (selectedTeamId && (selectionMode === 'individual' || selectionMode === 'both')) {
      loadTeamMembers(selectedTeamId)
    } else {
      setTeamMembers([])
      onUserChange(null)
      if (onMultipleUsersChange) {
        onMultipleUsersChange([])
      }
    }
  }, [selectedTeamId, selectionMode])

  const loadTeams = async () => {
    try {
      setLoadingTeams(true)
      setError(null)
      const response = await apiClient.getTeams()
      
      if (response.success && response.data?.teams) {
        setTeams(response.data.teams)
      } else {
        throw new Error(response.error || 'Failed to load teams')
      }
    } catch (err) {
      console.error('Failed to load teams:', err)
      setError('Failed to load teams')
    } finally {
      setLoadingTeams(false)
    }
  }

  const loadTeamMembers = async (teamId: string) => {
    try {
      setLoadingMembers(true)
      setError(null)
      
      console.log(`🔍 DEBUG: loadTeamMembers called with teamId=${teamId}, excludeCurrentUser=${excludeCurrentUser}`)
      
      const response = await apiClient.getTeamMembers(teamId, excludeCurrentUser)
      
      console.log('🔍 DEBUG: API response:', response)
      
      if (response.success && response.data?.members) {
        console.log(
          `🔍 DEBUG: Setting ${response.data.members.length} team members:`,
          response.data.members.map((m: TeamMember) => ({ id: m.id, name: (m as any).name, email: m.email }))
        )
        setTeamMembers(response.data.members)
      } else {
        throw new Error(response.error || 'Failed to load team members')
      }
    } catch (err) {
      console.error('Failed to load team members:', err)
      setError('Failed to load team members')
      setTeamMembers([])
    } finally {
      setLoadingMembers(false)
    }
  }

  const handleTeamChange = (teamId: string) => {
    const id = teamId || null
    onTeamChange(id)
  }

  const handleUserChange = (userId: string) => {
    const id = userId || null
    onUserChange(id)
  }

  const handleMultipleUserToggle = (userId: string) => {
    if (!onMultipleUsersChange) return
    
    const currentIds = selectedUserIds || []
    const isSelected = currentIds.includes(userId)
    
    if (isSelected) {
      onMultipleUsersChange(currentIds.filter(id => id !== userId))
    } else {
      onMultipleUsersChange([...currentIds, userId])
    }
  }

  const getSelectedTeam = () => {
    return teams.find(team => team.id === selectedTeamId)
  }

  const getSelectedUser = () => {
    return teamMembers.find(member => member.id === selectedUserId)
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">{error}</p>
          <button
            onClick={loadTeams}
            className="mt-2 text-sm text-red-700 hover:text-red-800 underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* Team Selection */}
      {(selectionMode === 'team' || selectionMode === 'both') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {loadingTeams ? (
            <div className="flex items-center justify-center py-3 border border-gray-300 rounded-lg">
              <Loader className="w-5 h-5 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-500">Loading teams...</span>
            </div>
          ) : (
            <div className="relative">
              <select
                value={selectedTeamId || ''}
                onChange={(e) => handleTeamChange(e.target.value)}
                disabled={disabled}
                required={required}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed appearance-none"
              >
                <option value="">{placeholder}</option>
                {teams.map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name} ({team.member_count} members) - {team.user_role}
                  </option>
                ))}
              </select>
              <Users className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
            </div>
          )}
          
          {selectedTeamId && (
            <p className="mt-1 text-xs text-gray-500">
              Only teams you're a member of or lead are shown
            </p>
          )}
        </div>
      )}

      {/* Individual Member Selection */}
      {(selectionMode === 'individual' || selectionMode === 'both') && selectedTeamId && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Team Member{allowMultipleUsers ? 's' : ''}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
          
          {loadingMembers ? (
            <div className="flex items-center justify-center py-3 border border-gray-300 rounded-lg">
              <Loader className="w-5 h-5 animate-spin text-blue-600" />
              <span className="ml-2 text-sm text-gray-500">Loading team members...</span>
            </div>
          ) : teamMembers.length > 0 ? (
            allowMultipleUsers ? (
              <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                {teamMembers.map((member) => {
                  const isSelected = selectedUserIds?.includes(member.id) || false
                  return (
                    <label
                      key={member.id}
                      className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleMultipleUserToggle(member.id)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="ml-3 flex-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">
                              {member.first_name} {member.last_name}
                            </p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                          {showUserRole && (
                            <div className="text-right">
                              <p className="text-xs text-gray-600">{member.team_role}</p>
                              {member.title && (
                                <p className="text-xs text-gray-500">{member.title}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  )
                })}
              </div>
            ) : (
              <div className="relative">
                <select
                  value={selectedUserId || ''}
                  onChange={(e) => handleUserChange(e.target.value)}
                  disabled={disabled}
                  required={required && selectionMode === 'individual'}
                  className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-50 disabled:cursor-not-allowed appearance-none"
                >
                  <option value="">Select a team member...</option>
                  {teamMembers.map((member) => (
                    <option key={member.id} value={member.id}>
                      {member.first_name} {member.last_name} ({member.email})
                      {showUserRole && ` - ${member.team_role}`}
                    </option>
                  ))}
                </select>
                <User className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
                <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none" />
              </div>
            )
          ) : (
            <div className="text-center py-6 border border-gray-200 rounded-lg bg-gray-50">
              <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
              <p className="text-sm text-gray-500">No team members found</p>
            </div>
          )}
        </div>
      )}

      {/* Selected Information Display */}
      {(selectedTeamId || selectedUserId) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <h4 className="text-sm font-medium text-blue-900 mb-1">Selection Summary:</h4>
          <div className="text-sm text-blue-700 space-y-1">
            {selectedTeamId && (
              <p>
                <span className="font-medium">Team:</span> {getSelectedTeam()?.name}
                {getSelectedTeam()?.member_count && ` (${getSelectedTeam()?.member_count} members)`}
              </p>
            )}
            {selectedUserId && !allowMultipleUsers && (
              <p>
                <span className="font-medium">Member:</span> {getSelectedUser()?.first_name} {getSelectedUser()?.last_name}
              </p>
            )}
            {allowMultipleUsers && selectedUserIds && selectedUserIds.length > 0 && (
              <p>
                <span className="font-medium">Members:</span> {selectedUserIds.length} selected
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
